import type { ReactElement } from 'react';
import { useRecentActivity, type RecentActivityEntry } from '@/lib/queries/admin';
import { actionLabel, actionTone, type AdminActionTone } from '@/lib/admin-action-labels';
import { AdminCardError } from '@/components/atoms/admin-card-error';

const ENTRY_LIMIT = 7;

export function AdminRecentActivityCard(): ReactElement {
  const { data, isError } = useRecentActivity();

  if (isError) return <AdminCardError label="recent activity" />;
  if (!data) return <CardShell><SkeletonRows count={3} /></CardShell>;

  const entries = data.slice(0, ENTRY_LIMIT);

  return (
    <CardShell>
      {entries.length === 0 ? (
        <EmptyRow message="No activity yet" />
      ) : (
        <ul className="divide-y divide-[var(--rule)]">
          {entries.map((e) => (
            <li key={e.adminAuditLogId} className="px-4 py-2.5 flex items-center gap-3">
              <ActionChip action={e.action} />
              <span className="text-[12px] truncate flex-1">{e.summary ?? '—'}</span>
              {e.actorHandle && (
                <span className="text-[11px] text-[var(--ink-3)] shrink-0">{e.actorHandle}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </CardShell>
  );
}

function CardShell({ children }: { children: React.ReactNode }): ReactElement {
  return (
    <section className="rounded-[var(--r-md)] border border-[var(--rule)] bg-[var(--bg-surface)] overflow-hidden">
      <header className="px-4 py-3 flex items-center justify-between border-b border-[var(--rule)]">
        <h2 className="text-[13px] font-semibold">Recent activity</h2>
        <span
          aria-disabled="true"
          title="Coming soon"
          className="text-[12px] text-[var(--ink-4)] cursor-not-allowed"
        >
          Open audit log →
        </span>
      </header>
      {children}
    </section>
  );
}

const CHIP_TONE_CLASS: Readonly<Record<AdminActionTone, string>> = {
  ok: 'activity-chip--ok bg-[var(--ok-wash)] text-[var(--ok)]',
  err: 'activity-chip--err bg-[var(--err-wash)] text-[var(--err)]',
  warn: 'activity-chip--warn bg-[var(--warn-wash)] text-[var(--warn)]',
  neutral: 'activity-chip--neutral bg-[var(--bg-soft)] text-[var(--ink-3)]',
};

function ActionChip({ action }: { action: RecentActivityEntry['action'] }): ReactElement {
  return (
    <span
      className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${CHIP_TONE_CLASS[actionTone(action)]}`}
    >
      {actionLabel(action)}
    </span>
  );
}

function EmptyRow({ message }: { message: string }): ReactElement {
  return (
    <div className="px-4 py-6 text-[12px] text-[var(--ink-3)] text-center">{message}</div>
  );
}

function SkeletonRows({ count }: { count: number }): ReactElement {
  return (
    <ul className="divide-y divide-[var(--rule)]">
      {Array.from({ length: count }).map((_, i) => (
        <li key={i} className="px-4 py-3">
          <div className="h-3 w-2/3 rounded bg-[var(--bg-soft)]" />
        </li>
      ))}
    </ul>
  );
}
